import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { facilityDirectory, inFacilityScope, isAllFacilities, scopeFacilities, type DirectoryRow, type FacilityScope } from '@aithora/shared';
import { useQuery, type QueryState } from '../useQuery';
import { useSession } from './SessionContext';

/**
 * What Operator and Super Admin are looking at (V6 facility switcher): a country, a region,
 * a state, a city or one building. The choice is the viewer's own and stays in this
 * browser. Pages that follow it read useFacilityScope(); the rest say they show every facility.
 */
const KEY = 'aithora.facilityScope';

function readScope(): FacilityScope {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) ?? '{}');
    if (!v || typeof v !== 'object') return {};
    const out: FacilityScope = {};
    for (const k of ['country', 'region', 'state', 'city', 'fac'] as const) if (typeof v[k] === 'string' && v[k]) out[k] = v[k];
    return out;
  } catch {
    return {};
  }
}

export interface FacilityScopeValue {
  /** True for the roles the switcher is for. Everyone else always sees their own building. */
  enabled: boolean;
  scope: FacilityScope;
  setScope: (s: FacilityScope) => void;
  directory: QueryState<{ facilities: DirectoryRow[]; canEditRegions: boolean }>;
  /** The buildings in view (all of them when nothing is picked). */
  inView: DirectoryRow[];
  /** Whether a building, by name, is in view. A name the directory does not know is out once something is picked. */
  includes: (name: string) => boolean;
  /** After Super Admin moves a building, reload the list. */
  reload: () => void;
  /**
   * The open view is company-wide whatever is picked (design CO: Atlas Chat and Eval,
   * Finance › Aithora). Set by the page with useCompanyWideView; the switcher says so.
   */
  companyWide: boolean;
  setCompanyWide: (on: boolean) => void;
}

const Ctx = createContext<FacilityScopeValue | null>(null);

export function FacilityScopeProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const { client, session } = useSession();
  const [scope, setScopeState] = useState<FacilityScope>(() => (enabled ? readScope() : {}));
  const [companyWide, setCompanyWide] = useState(false);
  const directory = useQuery((signal) => facilityDirectory(client, { signal }), [client, session?.token], { enabled });
  const list = directory.data?.facilities;

  const setScope = useCallback((s: FacilityScope) => {
    setScopeState(s);
    try { window.localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode: this session only */ }
  }, []);

  // A remembered pick that no longer matches any building (renamed, moved) falls back to all.
  useEffect(() => {
    if (list && !isAllFacilities(scope) && scopeFacilities(list, scope).length === 0) setScope({});
  }, [list, scope, setScope]);

  const value = useMemo<FacilityScopeValue>(() => {
    const active = enabled ? scope : {};
    const rows = list ?? [];
    return {
      enabled,
      scope: active,
      setScope,
      directory,
      inView: scopeFacilities(rows, active) as DirectoryRow[],
      includes: (name: string) => !enabled || !list || inFacilityScope(rows, active, name),
      reload: directory.reload,
      companyWide,
      setCompanyWide,
    };
  }, [enabled, scope, list, directory, setScope, companyWide]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The picked buildings' ids when the switcher narrows the view, null when it does not (every
 * building, a building role, or the list not loaded yet). A page that follows the switcher
 * sends these as ?facilities= and keeps only these rows.
 */
export function usePickedIds(): string[] | null {
  const { enabled, scope, inView, directory } = useFacilityScope();
  return useMemo(
    () => (enabled && directory.data && !isAllFacilities(scope) ? inView.map((f) => f.id) : null),
    [enabled, directory.data, scope, inView],
  );
}

/**
 * A pick that matches none of our buildings still narrows: the server answers it empty (null
 * figures, no rows), never every building. This id is no organization.
 */
export const NO_BUILDING_ID = '00000000-0000-4000-8000-000000000000';

/** The ?facilities= value for a pick, or undefined when nothing is picked. */
export function facilitiesParam(pick: string[] | null): string | undefined {
  if (!pick) return undefined;
  return pick.length ? pick.join(',') : NO_BUILDING_ID;
}

/** A query's data narrowed by `pick` (unchanged when pick is null). */
export function narrowQuery<T>(q: QueryState<T>, pick: string[] | null, fn: (d: T, ids: Set<string>) => T): QueryState<T> {
  if (!pick || !q.data) return q;
  return { ...q, data: fn(q.data, new Set(pick)) };
}

/** The switcher's choice. Outside the provider (tests, pages without a shell) nothing is narrowed. */
export function useFacilityScope(): FacilityScopeValue {
  const v = useContext(Ctx);
  if (v) return v;
  return {
    enabled: false,
    scope: {},
    setScope: () => {},
    directory: { data: null, error: null, loading: false, reload: () => {}, setData: () => {} },
    inView: [],
    includes: () => true,
    reload: () => {},
    companyWide: false,
    setCompanyWide: () => {},
  };
}

/**
 * A view inside a page that is company-wide whatever the switcher picks (Atlas Chat and Eval,
 * Finance › Aithora): while it is open, the switcher's note says "Company-wide page".
 */
export function useCompanyWideView(on: boolean): void {
  const { setCompanyWide } = useFacilityScope();
  useEffect(() => {
    setCompanyWide(on);
    return () => setCompanyWide(false);
  }, [on, setCompanyWide]);
}
